import React, { useEffect, useMemo, useRef, useState } from 'react';
import manageareas from '../services/manageareas';
import managetables from '../services/managetables';
import { hasLive } from '../services/plan';
import TablePage from './TablePage';
import Icon from '../comps/Icon';
import Loading from '../comps/Loading';
import '../style/css/tablesmanager2026.css';

/*
 * TablesManager — a LIST-BASED table & area manager for HOPpA.
 *
 * Every plan can create/edit/delete tables and areas here WITHOUT the (broken)
 * Konva sketch. Only live+ plans (hasLive) get an extra "סקיצה" tab that renders
 * the existing visual <TablePage>. All writes hit the REAL PHP acts through
 * manageareas / managetables and re-fetch afterwards so the UI mirrors the server.
 */

// ---- helpers -------------------------------------------------------------

// Areas from PHP arrive as { id, ID, name, subToID }. Normalise the id both
// ways (id primary, ID fallback) and coerce subToID for reliable grouping.
const areaId = (a) => Number(a.id ?? a.ID);

// A "main" area is one with no parent (subToID 0 / null / ""). Sub-areas point
// at a parent via subToID. subToID is normally the parent id, but tolerate it
// being stored as the parent NAME too.
const isMainArea = (a) => {
    const s = a.subToID;
    return s === 0 || s === null || s === undefined || s === '0' || s === '';
};

const matchesParent = (child, parent) => {
    if (isMainArea(child)) return false;
    const s = child.subToID;
    if (Number(s) === areaId(parent)) return true;                 // by id (primary)
    if (typeof s === 'string' && s && s === parent.name) return true; // by name (tolerated)
    return false;
};

// ---- component -----------------------------------------------------------

const TablesManager = ({ areaLocked }) => {
    const [areas, setAreas] = useState([]);
    const [tables, setTables] = useState([]);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState(null); // { kind:'ok'|'err', msg }
    const [tab, setTab] = useState('list');    // 'list' | 'sketch' (sketch only for live+)
    const [sketchArea, setSketchArea] = useState(0); // sub-area shown in the sketch tab (TablePage needs a sub-area to edit)

    // add-table form
    const [addName, setAddName] = useState('');
    const [addArea, setAddArea] = useState('');
    const [addSeats, setAddSeats] = useState('4');
    const [addHold, setAddHold] = useState(''); // per-table hold time (זמן תפוסה); '' = business default
    const [busyAdd, setBusyAdd] = useState(false);

    // inline table edit
    const [editId, setEditId] = useState(null);
    const [editName, setEditName] = useState('');
    const [editSeats, setEditSeats] = useState('');
    const [editHold, setEditHold] = useState(''); // per-table hold time; '' = business default

    // area add / edit
    const [showAreaAdd, setShowAreaAdd] = useState(false);
    const [areaName, setAreaName] = useState('');
    const [areaParent, setAreaParent] = useState('');
    const [editAreaId, setEditAreaId] = useState(null);
    const [editAreaName, setEditAreaName] = useState('');

    const mounted = useRef(true);
    const live = hasLive();

    // Sub-areas (leaf areas) — the sketch editor (TablePage) can only edit a sub-area.
    const subAreas = useMemo(() => areas.filter((a) => !isMainArea(a)), [areas]);
    useEffect(() => {
        if (!sketchArea && subAreas.length) setSketchArea(areaId(subAreas[0]));
    }, [subAreas, sketchArea]);

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    // toast auto-dismiss
    useEffect(() => {
        if (!toast) return undefined;
        const t = setTimeout(() => { if (mounted.current) setToast(null); }, 3200);
        return () => clearTimeout(t);
    }, [toast]);

    const notify = (kind, msg) => { if (mounted.current) setToast({ kind, msg }); };

    const loadAll = async (withSpinner = false) => {
        if (withSpinner && mounted.current) setLoading(true);
        try {
            const [a, t] = await Promise.all([
                manageareas.getAllArea(true),
                managetables.getAllTables(true),
            ]);
            if (!mounted.current) return;
            setAreas(Array.isArray(a) ? a : []);
            setTables(Array.isArray(t) ? t : []);
        } catch (e) {
            console.error(e);
            if (mounted.current) notify('err', 'שגיאה בטעינת הנתונים');
        } finally {
            if (mounted.current) setLoading(false);
        }
    };

    useEffect(() => { loadAll(true); }, []);

    const refreshTables = async () => {
        const t = await managetables.getAllTables(true);
        if (mounted.current) setTables(Array.isArray(t) ? t : []);
    };
    const refreshAreas = async () => {
        const a = await manageareas.getAllArea(true);
        if (mounted.current) setAreas(Array.isArray(a) ? a : []);
    };

    // ---- derived: tables per area, seat sums, grouping ----
    const tablesByArea = useMemo(() => {
        const map = new Map();
        tables.forEach((t) => {
            const key = Number(t.area_id);
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(t);
        });
        return map;
    }, [tables]);

    const tablesOf = (a) => tablesByArea.get(areaId(a)) || [];
    const seatsOf = (a) => tablesOf(a).reduce((sum, t) => sum + (Number(t.diners) || 0), 0);

    const mainAreas = useMemo(() => areas.filter(isMainArea), [areas]);
    const subAreasOf = (parent) => areas.filter((c) => matchesParent(c, parent));
    // orphans: sub-areas whose parent isn't in the list (still show them so nothing is lost)
    const orphanAreas = useMemo(() => areas.filter((c) => {
        if (isMainArea(c)) return false;
        return !areas.some((p) => matchesParent(c, p));
    }), [areas]);

    // Prefer leaf/sub-areas for the add-table select, but list all so any area works.
    const areaOptions = useMemo(() => {
        const subs = areas.filter((a) => !isMainArea(a));
        const mains = areas.filter(isMainArea);
        return [...subs, ...mains];
    }, [areas]);

    // ---- table CRUD ----
    const handleAddTable = async (e) => {
        e.preventDefault();
        if (busyAdd) return;
        const name = addName.trim();
        const targetArea = Number(addArea);
        if (!name) { notify('err', 'יש להזין שם שולחן'); return; }
        if (!targetArea) { notify('err', 'יש לבחור אזור'); return; }
        const seats = Math.max(1, parseInt(addSeats, 10) || 1);
        // Optional per-table hold time: a positive int overrides; empty/0 => business default.
        const holdParsed = parseInt(addHold, 10);
        const holdTime = Number.isFinite(holdParsed) && holdParsed > 0 ? holdParsed : null;
        const existingCount = (tablesByArea.get(targetArea) || []).length;
        const x = 80 + (existingCount % 5) * 120;
        const y = 80 + Math.floor(existingCount / 5) * 120;

        setBusyAdd(true);
        try {
            const res = await managetables.addTable({
                name,
                area_id: targetArea,
                diners: seats,
                holdTime,
                shape: 'square',
                is_locked: 0,
                x,
                y,
            });
            if (!res) { notify('err', 'הוספת השולחן נכשלה'); return; }
            await refreshTables();
            notify('ok', 'השולחן נוסף');
            setAddName('');
            setAddSeats('4');
            setAddHold('');
        } finally {
            if (mounted.current) setBusyAdd(false);
        }
    };

    const startEditTable = (t) => {
        setEditId(t.id);
        setEditName(t.name || '');
        setEditSeats(String(t.diners ?? 1));
        // Prefill hold time only when the table has a real override; NULL => empty (default).
        setEditHold(Number(t.holdTime) > 0 ? String(t.holdTime) : '');
    };
    const cancelEditTable = () => { setEditId(null); setEditName(''); setEditSeats(''); setEditHold(''); };

    const saveEditTable = async (t) => {
        const name = editName.trim();
        if (!name) { notify('err', 'יש להזין שם שולחן'); return; }
        const seats = Math.max(1, parseInt(editSeats, 10) || 1);
        // Optional per-table hold time: positive int overrides; empty/0 => business default (null).
        const holdParsed = parseInt(editHold, 10);
        const holdTime = Number.isFinite(holdParsed) && holdParsed > 0 ? holdParsed : null;
        const res = await managetables.updateTable({ ...t, name, diners: seats, holdTime });
        if (!res) { notify('err', 'עדכון השולחן נכשל'); return; }
        cancelEditTable();
        await refreshTables();
        notify('ok', 'השולחן עודכן');
    };

    const handleDeleteTable = async (t) => {
        // eslint-disable-next-line no-restricted-globals
        if (!window.confirm(`למחוק את השולחן "${t.name}"?`)) return;
        const res = await managetables.deleteTable(t.id);
        if (!res) { notify('err', 'מחיקת השולחן נכשלה'); return; }
        await refreshTables();
        notify('ok', 'השולחן נמחק');
    };

    // ---- area CRUD ----
    const handleAddArea = async (e) => {
        e.preventDefault();
        const name = areaName.trim();
        if (!name) { notify('err', 'יש להזין שם אזור'); return; }
        try {
            await manageareas.addArea(name, Number(areaParent) || 0);
            await refreshAreas();
            notify('ok', 'האזור נוסף');
            setAreaName('');
            setAreaParent('');
            setShowAreaAdd(false);
        } catch (err) {
            console.error(err);
            notify('err', 'הוספת האזור נכשלה');
        }
    };

    const startEditArea = (a) => { setEditAreaId(areaId(a)); setEditAreaName(a.name || ''); };
    const cancelEditArea = () => { setEditAreaId(null); setEditAreaName(''); };

    const saveEditArea = async (a) => {
        const name = editAreaName.trim();
        if (!name) { notify('err', 'יש להזין שם אזור'); return; }
        try {
            await manageareas.editArea(areaId(a), a.subToID ?? 0, name);
            cancelEditArea();
            await refreshAreas();
            notify('ok', 'האזור עודכן');
        } catch (err) {
            console.error(err);
            notify('err', 'עדכון האזור נכשל');
        }
    };

    const handleDeleteArea = async (a) => {
        const count = tablesOf(a).length;
        const subCount = subAreasOf(a).length;
        if (count > 0 || subCount > 0) {
            notify('err', 'צריך קודם למחוק/להעביר את השולחנות באזור');
            return;
        }
        // eslint-disable-next-line no-restricted-globals
        if (!window.confirm(`למחוק את האזור "${a.name}"?`)) return;
        try {
            await manageareas.delArea(areaId(a));
            await refreshAreas();
            notify('ok', 'האזור נמחק');
        } catch (err) {
            // PHP returns 408 (non-ok) when the area still has active tables.
            console.error(err);
            notify('err', 'לא ניתן למחוק אזור עם שולחנות — העבירו או מחקו קודם את השולחנות');
        }
    };

    // ---- renders ----
    const renderTableRow = (t) => {
        const editing = editId === t.id;
        return (
            <li key={t.id} className="tm-table-row">
                {editing ? (
                    <div className="tm-table-edit">
                        <input
                            className="tm-input tm-input-name"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            placeholder="שם שולחן"
                            aria-label="שם שולחן"
                            autoFocus
                        />
                        <input
                            className="tm-input tm-input-seats"
                            type="number"
                            min="1"
                            value={editSeats}
                            onChange={(e) => setEditSeats(e.target.value)}
                            aria-label="מספר מקומות"
                        />
                        <input
                            className="tm-input tm-input-hold"
                            type="number"
                            min="5"
                            max="600"
                            value={editHold}
                            onChange={(e) => setEditHold(e.target.value)}
                            placeholder="זמן תפוסה"
                            title="זמן תפוסה (דקות) — ריק = ברירת מחדל של העסק"
                            aria-label="זמן תפוסה בדקות"
                        />
                        <button type="button" className="tm-icon-btn tm-ok" onClick={() => saveEditTable(t)} aria-label="שמירה">
                            <Icon name="check" size={18} />
                        </button>
                        <button type="button" className="tm-icon-btn" onClick={cancelEditTable} aria-label="ביטול">
                            <Icon name="x" size={18} />
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="tm-table-info">
                            <span className="tm-table-name">{t.name}</span>
                            <span className="tm-table-seats">
                                <Icon name="users" size={15} /> {Number(t.diners) || 0}
                            </span>
                            {Number(t.holdTime) > 0 && (
                                <span className="tm-table-hold" title="זמן תפוסה מותאם לשולחן">
                                    <Icon name="clock" size={14} /> {Number(t.holdTime)}׳
                                </span>
                            )}
                        </div>
                        <div className="tm-table-acts">
                            <button type="button" className="tm-icon-btn" onClick={() => startEditTable(t)} aria-label="עריכת שולחן">
                                <Icon name="edit" size={17} />
                            </button>
                            <button type="button" className="tm-icon-btn tm-danger" onClick={() => handleDeleteTable(t)} aria-label="מחיקת שולחן">
                                <Icon name="trash" size={17} />
                            </button>
                        </div>
                    </>
                )}
            </li>
        );
    };

    const renderArea = (a, isSub = false) => {
        const list = tablesOf(a);
        const editing = editAreaId === areaId(a);
        const hasContent = list.length > 0 || subAreasOf(a).length > 0;
        return (
            <div key={`area-${areaId(a)}`} className={`tm-area ${isSub ? 'tm-area--sub' : ''}`}>
                <div className="tm-area-head">
                    <div className="tm-area-title">
                        <Icon name={isSub ? 'chevron-left' : 'layers'} size={18} />
                        {editing ? (
                            <input
                                className="tm-input tm-input-area"
                                value={editAreaName}
                                onChange={(e) => setEditAreaName(e.target.value)}
                                aria-label="שם אזור"
                                autoFocus
                            />
                        ) : (
                            <span className="tm-area-name">{a.name}</span>
                        )}
                    </div>
                    <div className="tm-area-meta">
                        <span className="tm-badge"><Icon name="tables" size={14} /> {list.length} שולחנות</span>
                        <span className="tm-badge tm-badge--seats">
                            <Icon name="users" size={14} /> {seatsOf(a)} מקומות פנויים
                        </span>
                        <div className="tm-area-acts">
                            {editing ? (
                                <>
                                    <button type="button" className="tm-icon-btn tm-ok" onClick={() => saveEditArea(a)} aria-label="שמירת אזור">
                                        <Icon name="check" size={16} />
                                    </button>
                                    <button type="button" className="tm-icon-btn" onClick={cancelEditArea} aria-label="ביטול">
                                        <Icon name="x" size={16} />
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button type="button" className="tm-icon-btn" onClick={() => startEditArea(a)} aria-label="עריכת אזור">
                                        <Icon name="edit" size={16} />
                                    </button>
                                    <button
                                        type="button"
                                        className="tm-icon-btn tm-danger"
                                        onClick={() => handleDeleteArea(a)}
                                        disabled={hasContent}
                                        title={hasContent ? 'לא ניתן למחוק אזור עם שולחנות או תת-אזורים' : 'מחיקת אזור'}
                                        aria-label="מחיקת אזור"
                                    >
                                        <Icon name="trash" size={16} />
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                {hasContent && list.length > 0 && (
                    <ul className="tm-table-list">{list.map(renderTableRow)}</ul>
                )}
                {list.length === 0 && subAreasOf(a).length === 0 && (
                    <p className="tm-empty-mini">אין שולחנות באזור זה</p>
                )}
                {/* nested sub-areas */}
                {subAreasOf(a).map((sub) => renderArea(sub, true))}
            </div>
        );
    };

    if (loading) return <Loading />;

    const noAreas = areas.length === 0;

    return (
        <div className="tm-page" dir="rtl">
            <header className="tm-header">
                <div className="tm-header-text">
                    <h1 className="tm-title">ניהול שולחנות</h1>
                    <p className="tm-subtitle">הוספה, עריכה ומחיקה של אזורים ושולחנות{live ? '' : ' — ניהול רשימתי'}</p>
                </div>
                {live && (
                    <div className="tm-tabs" role="tablist">
                        <button
                            type="button"
                            role="tab"
                            aria-selected={tab === 'list'}
                            className={`tm-tab ${tab === 'list' ? 'tm-tab--active' : ''}`}
                            onClick={() => setTab('list')}
                        >
                            <Icon name="list" size={17} /> רשימה
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={tab === 'sketch'}
                            className={`tm-tab ${tab === 'sketch' ? 'tm-tab--active' : ''}`}
                            onClick={() => setTab('sketch')}
                        >
                            <Icon name="image" size={17} /> סקיצה
                        </button>
                    </div>
                )}
            </header>

            {live && tab === 'sketch' ? (
                <div className="tm-sketch-wrap">
                    <div className="tm-sketch-bar">
                        <label className="tm-sketch-arealabel">תת-אזור לעריכה:</label>
                        <select className="tm-sketch-areasel" value={sketchArea}
                            onChange={(e) => setSketchArea(Number(e.target.value))}>
                            {subAreas.length === 0 && <option value={0}>הוסיפו קודם תת-אזור</option>}
                            {subAreas.map((a) => <option key={areaId(a)} value={areaId(a)}>{a.name}</option>)}
                        </select>
                    </div>
                    <TablePage areaLocked={sketchArea || areaLocked} />
                </div>
            ) : (
                <div className="tm-body">
                    {/* Add table */}
                    <section className="tm-card tm-add-card">
                        <h2 className="tm-card-title"><Icon name="plus" size={18} /> הוספת שולחן</h2>
                        <form className="tm-add-form" onSubmit={handleAddTable}>
                            <label className="tm-field">
                                <span className="tm-field-label">שם השולחן</span>
                                <input
                                    className="tm-input"
                                    value={addName}
                                    onChange={(e) => setAddName(e.target.value)}
                                    placeholder="לדוגמה: שולחן 12"
                                />
                            </label>
                            <label className="tm-field">
                                <span className="tm-field-label">אזור</span>
                                <select
                                    className="tm-input tm-select"
                                    value={addArea}
                                    onChange={(e) => setAddArea(e.target.value)}
                                >
                                    <option value="">בחרו אזור…</option>
                                    {areaOptions.map((a) => (
                                        <option key={areaId(a)} value={areaId(a)}>
                                            {isMainArea(a) ? a.name : `↳ ${a.name}`}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label className="tm-field tm-field--seats">
                                <span className="tm-field-label">מקומות</span>
                                <input
                                    className="tm-input"
                                    type="number"
                                    min="1"
                                    value={addSeats}
                                    onChange={(e) => setAddSeats(e.target.value)}
                                />
                            </label>
                            <label className="tm-field tm-field--hold">
                                <span className="tm-field-label">זמן תפוסה (דקות)</span>
                                <input
                                    className="tm-input"
                                    type="number"
                                    min="5"
                                    max="600"
                                    value={addHold}
                                    onChange={(e) => setAddHold(e.target.value)}
                                    placeholder="ריק = ברירת מחדל של העסק"
                                    title="ריק = ברירת מחדל של העסק"
                                />
                            </label>
                            <button type="submit" className="tm-btn tm-btn--primary" disabled={busyAdd || noAreas}>
                                <Icon name="plus" size={17} /> הוספה
                            </button>
                        </form>
                        {noAreas && <p className="tm-hint">צריך קודם להוסיף אזור כדי לשייך אליו שולחנות</p>}
                    </section>

                    {/* Areas toolbar */}
                    <section className="tm-card">
                        <div className="tm-card-head">
                            <h2 className="tm-card-title"><Icon name="layers" size={18} /> אזורים ושולחנות</h2>
                            <button
                                type="button"
                                className="tm-btn tm-btn--ghost"
                                onClick={() => setShowAreaAdd((v) => !v)}
                            >
                                <Icon name={showAreaAdd ? 'x' : 'plus'} size={16} />
                                {showAreaAdd ? 'סגירה' : 'הוספת אזור'}
                            </button>
                        </div>

                        {showAreaAdd && (
                            <form className="tm-area-add" onSubmit={handleAddArea}>
                                <input
                                    className="tm-input"
                                    value={areaName}
                                    onChange={(e) => setAreaName(e.target.value)}
                                    placeholder="שם האזור (למשל: מרפסת)"
                                />
                                <select
                                    className="tm-input tm-select"
                                    value={areaParent}
                                    onChange={(e) => setAreaParent(e.target.value)}
                                >
                                    <option value="">אזור ראשי (ללא הורה)</option>
                                    {mainAreas.map((a) => (
                                        <option key={areaId(a)} value={areaId(a)}>תת-אזור של: {a.name}</option>
                                    ))}
                                </select>
                                <button type="submit" className="tm-btn tm-btn--primary">
                                    <Icon name="check" size={16} /> יצירה
                                </button>
                            </form>
                        )}

                        {noAreas ? (
                            <div className="tm-empty">
                                <Icon name="layers" size={40} />
                                <p>עדיין לא הוגדרו אזורים/שולחנות — הוסיפו כדי להתחיל</p>
                            </div>
                        ) : (
                            <div className="tm-areas">
                                {mainAreas.map((a) => renderArea(a, false))}
                                {orphanAreas.map((a) => renderArea(a, false))}
                            </div>
                        )}
                    </section>
                </div>
            )}

            {toast && (
                <div className={`tm-toast ${toast.kind === 'err' ? 'tm-toast--err' : 'tm-toast--ok'}`} role="status">
                    <Icon name={toast.kind === 'err' ? 'info' : 'check'} size={16} />
                    <span>{toast.msg}</span>
                </div>
            )}
        </div>
    );
};

export default TablesManager;
